import React from "react";
import "./size-select-field.scss";
import { SizeSelectInput } from "./SizeSelectInput";
import { SizeSelectPanel } from "./SizeSelectPanel";
import { sizes } from "../../../common/sizes";
import { Dropdown } from "../Dropdown";

const isSetsEqual = (a: any, b: any) => {
  let isSameSize = a.size === b.size;
  let hasSameValues = [...a].every(value => b.includes(value));
  return isSameSize && hasSameValues;
};

export interface SizeSelectFieldSize {
  label: string;
  sizeId: string;
}

interface SizeSelectFieldState {
  open: boolean;
  appliedSizeFilters: Set<string>;
  selectedSizes: Set<string>;
}

export interface SizeSelectFieldProps {
  initialAppliedSizes?: string[];
  filter?: (size: SizeSelectFieldSize) => boolean;
  sizes?: SizeSelectFieldSize[];
  onChange?: (sizes: string[]) => void;
  onApply?: (sizes: string[]) => void;
}

export class SizeSelectField extends React.Component<SizeSelectFieldProps, SizeSelectFieldState> {
  static defaultProps = {
    sizes: sizes
  };

  constructor(props: SizeSelectFieldProps) {
    super(props);
    this.state = {
      open: false,
      appliedSizeFilters: new Set(this.props.initialAppliedSizes) || new Set(),
      selectedSizes: new Set()
    };
  }

  componentDidUpdate(
    prevProps: Readonly<SizeSelectFieldProps>,
    prevState: Readonly<SizeSelectFieldState>,
    snapshot?: any
  ): void {
    if (this.props.onChange) {
      this.props.onChange(Array.from(this.state.appliedSizeFilters));
    }

    // For updating sizes
    let propAppliedSizes = this.props.initialAppliedSizes;
    let prevAppliedSizes = prevProps.initialAppliedSizes;
    if (propAppliedSizes && prevAppliedSizes && !isSetsEqual(propAppliedSizes, prevAppliedSizes)) {
      this.setState({
        appliedSizeFilters: new Set(this.props.initialAppliedSizes)
      });
    }
  }

  render() {
    const availableSizes = this.getAvailableSizes();

    return (
      <div className="c-new-size-select-field">
        <SizeSelectInput
          focused={this.state.open}
          sizes={availableSizes}
          selectedSizeIds={this.state.appliedSizeFilters}
          onClick={this.handleFieldClick}
        />
        <Dropdown
          visible={this.state.open}
          onClickOutsideDropdown={() => {
            return this.setState({ open: false });
          }}
        >
          <SizeSelectPanel
            sizes={availableSizes}
            selectedSizeIds={this.state.selectedSizes}
            onApply={this.handleApply}
            onClose={() => this.setState({ open: false })}
            onDeselectSizeId={this.handleDeselectSizeId}
            onSelectSizeId={this.handleSelectSizeId}
          />
        </Dropdown>
      </div>
    );
  }

  private handleFieldClick = () => {
    this.setState(prevState => ({
      open: !prevState.open,
      selectedSizes: new Set(prevState.appliedSizeFilters)
    }));
  };

  private handleApply = () => {
    this.setState({
      open: false,
      appliedSizeFilters: new Set(this.state.selectedSizes)
    });

    if (this.props.onApply) {
      let selected: string[] = [];
      this.state.selectedSizes.forEach(size => {
        selected.push(size);
      });
      this.props.onApply(selected);
    }
  };

  private handleSelectSizeId = (sizeId: string) => {
    this.setState((prev: SizeSelectFieldState) => {
      const selectedSizes: Set<string> = prev.selectedSizes.add(sizeId);
      const newState: Partial<SizeSelectFieldState> = {
        selectedSizes
      };

      return newState as SizeSelectFieldState;
    });
  };

  private handleDeselectSizeId = (sizeId: string) => {
    this.setState((prev: SizeSelectFieldState) => {
      prev.selectedSizes.delete(sizeId);
      const newState: Partial<SizeSelectFieldState> = {
        selectedSizes: prev.selectedSizes
      };

      return newState as SizeSelectFieldState;
    });
  };

  private getAvailableSizes() {
    if (this.props.filter) {
      return sizes.filter(this.props.filter);
    } else if (this.props.sizes) {
      return this.props.sizes;
    } else {
      return sizes;
    }
  }
}
